import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Helmet } from 'react-helmet-async'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import ScaleText from 'react-scale-text'
import { Waypoint } from 'react-waypoint'

import {
  CButton,
  CCol,
  CFormInput,
  CLink,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CRow,
  CSpinner,
  CTooltip,
} from '@coreui/react-pro'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  mdiArrowLeft,
  mdiArrowTopLeft,
  mdiArrowUp,
  mdiChevronDown,
  mdiClose,
  mdiEmail,
  mdiMagnify,
  mdiPlusCircleOutline,
  mdiSend,
  mdiTrayArrowDown,
  mdiUnfoldLessHorizontal,
} from '@mdi/js'
import Icon from '@mdi/react'
import ObjectID from 'bson-objectid'
import { debounce, shuffle } from 'lodash'
import { useFeatureFlagPayload } from 'posthog-js/react'
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react'
import * as yup from 'yup'

import { ReactComponent as DesktopExperienceSketch } from '@assets/images/desktop-experience-sketch.svg'
import { ReactComponent as WomanWorkingImage } from '@assets/images/woman-working.svg'
import AppFooter from '@components/AppFooter'
import AppLogo from '@components/atoms/AppLogo'
import FormTemplateListItem from '@components/atoms/FormTemplateListItem'
import GeneratorInput from '@components/atoms/GeneratorInput'
import SearchInput from '@components/atoms/SearchInput'
import SkeletonLoader from '@components/atoms/SkeletonLoader'
import SubscriptionSwitch from '@components/atoms/SubscriptionSwitch'
import ChatMessageButton from '@components/organisms/ChatMessageButton'
import {
  azureX,
  battleshipGrey,
  black,
  carolinaBlue,
  culturedGrey,
  darkGreen,
  lightGrey,
  middleBlue,
  pictonBlue,
  white,
} from '@constants/colors'
import { promptAudiences, promptExamples, promptTopics, promptTypes } from '@constants/general'
import { bookADemoLink, hubSpotContactUsForm, uMorePricingLink } from '@constants/links'
import { useAuth } from '@contexts/authContext'
import { useSavedForm } from '@contexts/savedFormProvider'
import { useToast } from '@contexts/toastProvider'
import {
  TemplateType,
  useGenerateFormDataMutation,
  useGeneratePromptsMutation,
  useMeQuery,
  useSearchDefaultTemplatesLazyQuery,
  useSendSegmentEventMutation,
  useTemplateLazyQuery,
  useTemplatesQuery,
} from '@graphQLGenerated'
import { LoaderType } from '@interfaces/components/atoms/SkeletonLoader'
import { SubscriptionSwitchOnSubmit } from '@interfaces/components/atoms/SubscriptionSwitch'
import { DefaultTemplatesQueryElement, FormBuilderViews } from '@interfaces/general'
import { isCrawler, isValidJSON } from '@utils/general'
import {
  getActivityPreviewTimes,
  getFormBuilderEmail,
  incrementActivityPreviewTimes,
  setFormBuilderEmail,
} from '@utils/localStorage'
import { analytics, SegmentEvent } from '@utils/segment'

import './styles.scss'

type SignUpSource =
  | 'Home Page'
  | 'Add To Library'
  | 'Send To Client'
  | 'Download PDF'
  | 'Automatic'
  | 'Limit Reached'

const FormBuilder: FC = () => {
  const limit = 24

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { templateIdOrSlug } = useParams()
  const { search, pathname } = useLocation()
  const query = new URLSearchParams(search)
  const defaultViewType = query.get('view')
  const defaultSearch = query.get('search')

  const featureFlagPayload = useFeatureFlagPayload('dashboard-demo-title-experiment') as
    | { title: string; description: string }
    | undefined

  const toast = useToast()
  const auth = useAuth()
  const savedForm = useSavedForm()

  const [selectedType, setSelectedType] = useState(TemplateType.Worksheet)

  const {
    data: defaultLibraryData,
    loading: libraryLoading,
    fetchMore,
  } = useTemplatesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      isDefault: true,
      filter: {
        type: selectedType,
        skip: 0,
        limit,
      },
    },
  })
  const [fetchTemplateById] = useTemplateLazyQuery({ fetchPolicy: 'cache-and-network' })
  const [searchTemplates, { data: searchTemplateData, loading: searchLoading }] =
    useSearchDefaultTemplatesLazyQuery({ fetchPolicy: 'network-only' })
  const [generateFormData, { loading }] = useGenerateFormDataMutation()
  const [generatePrompts, { loading: promptsLoading, data: promptsData }] =
    useGeneratePromptsMutation()

  const schema = yup.object().shape({
    email: yup.string().email('Email provided is incorrect').required('Required field'),
  })
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<{ email: string }>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  })

  const [sendSegmentEvent] = useSendSegmentEventMutation()

  const { data: meData } = useMeQuery({ skip: !auth.token })

  const [viewType, setViewType] = useState<FormBuilderViews>(FormBuilderViews.library)
  const [formData, setFormData] = useState<DefaultTemplatesQueryElement>()
  const [searchText, setSearchText] = useState('')
  // const [desktopBannerVisible, setDesktopBannerVisible] = useState(true)
  const [signUpSource, setSignUpSource] = useState<SignUpSource>()
  const [signUpModalVisible, setSignUpModalVisible] = useState(false)
  const [enterEmailModalVisible, setEnterEmailModalVisible] = useState(false)
  const [searchDone, setSearchDone] = useState(false)
  const [selectedTopics, setSelectedTopics] = useState<Array<string>>([])
  const [otherSelected, setOtherSelected] = useState(false)
  const [searchInputFocused, setSearchInputFocused] = useState(false)

  const [generatorText, setGeneratorText] = useState('')

  const [selectedPromptType, setSelectedPromptType] = useState('')
  const [selectedAudiences, setSelectedAudiences] = useState<Array<string>>([])
  const [selectedPromptTopics, setSelectedPromptTopics] = useState<Array<string>>([])
  const [extraInfo, setExtraInfo] = useState('')
  const [chatMessageCurrentContent, setChatMessageCurrentContent] = useState<string>()
  const [shuffledDefaultLibraryData, setShuffledDefaultLibraryData] = useState<
    Array<DefaultTemplatesQueryElement>
  >([])
  const [promptHelperViewVisible, setPromptHelperViewVisible] = useState(false)
  const [showScrollToTopButton, setShowScrollToTopButton] = useState(false)

  const firstTimeTabChange = useRef(false)
  // used to show sign-up modal when user previews for the second time only (without blocking him)
  const previewTimes = useRef(0)

  const skip = useRef(0)

  const allTopics = useMemo(
    () => [
      'Anxiety',
      'Depression',
      'Trauma',
      'Self-Talk',
      'Breakups',
      'Abandonment',
      'Social Anxiety',
      'Friendship',
      'Communication',
      'Grief',
      'Positive Psychology',
      'Executive Functioning',
      'Vulnerability',
      'Romantic Conflict',
    ],
    [],
  )
  const OTHER = 'Other'

  const isUmoreUser = useMemo(() => meData?.me?.email?.includes('@umore.app'), [meData?.me?.email])

  const creator = useMemo(
    () =>
      new SurveyCreator({
        showLogicTab: false,
        showJSONEditorTab: isUmoreUser || false,
        showDesignerTab: true,
        showPreviewTab: true,
        previewOrientation: 'portrait',
        showInvisibleElementsInPreviewTab: false,
        showPagesInPreviewTab: false,
      }),
    [isUmoreUser],
  )
  creator.toolbox.orderedQuestions = [
    'markdown',
    ...creator.toolbox.itemNames.filter((item) => item !== 'markdown'),
  ]

  useEffect(() => {
    analytics.track(SegmentEvent.FORM_BUILDER_PAGE_VIEWED)

    if (getFormBuilderEmail() === null && !isCrawler()) {
      setEnterEmailModalVisible(true)
    }
  }, [])

  useEffect(() => {
    setShuffledDefaultLibraryData([])
  }, [selectedType])

  useEffect(() => {
    if (defaultLibraryData?.templates?.data) {
      setShuffledDefaultLibraryData((prev) => [
        ...prev,
        ...shuffle(
          defaultLibraryData?.templates?.data?.slice(
            Math.max(defaultLibraryData?.templates?.data.length - limit, 0),
          ),
        ),
      ])
    }
  }, [defaultLibraryData?.templates?.data, selectedType])

  useEffect(() => {
    if (defaultViewType && formData) {
      setViewType(FormBuilderViews.preview)
    } else if (pathname === '/demo-generator') {
      setViewType(FormBuilderViews.generator)
    } else if (pathname === '/demo') {
      setViewType(FormBuilderViews.library)
    }
  }, [pathname, defaultViewType, formData])

  useEffect(() => {
    if (meData?.me?.organization && !isUmoreUser) {
      navigate('/create', { replace: true })
    }
  }, [isUmoreUser, meData?.me?.organization, navigate])

  useEffect(() => {
    if (templateIdOrSlug && !formData) {
      const variables: Record<string, string> = {}
      if (ObjectID.isValid(templateIdOrSlug)) {
        variables.templateId = templateIdOrSlug
      } else {
        variables.slug = templateIdOrSlug
      }
      fetchTemplateById({
        variables,
        onCompleted: (data) => {
          setFormData(data.template)
          analytics.track(SegmentEvent.FORM_BUILDER_ACTIVITY_PREVIEWED, {
            formTitle: data.template?.title,
            formType: data.template?.type,
            isFirstPage: true,
            isFormGenerated: false,
          })
        },
      })
    }
  }, [templateIdOrSlug, formData, fetchTemplateById])

  useEffect(() => {
    if (defaultSearch) {
      setSearchInputFocused(true)
      setSearchText(defaultSearch)
      searchTemplates({
        variables: {
          term: defaultSearch,
          filter: {
            type: selectedType,
          },
        },
        onCompleted: () => {
          setSearchDone(true)
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedTopics.length && !enterEmailModalVisible) {
      searchTemplates({
        variables: {
          term: selectedTopics.join(' '),
          filter: {
            type: selectedType,
          },
        },
      })
    }
  }, [searchTemplates, selectedTopics, enterEmailModalVisible, selectedType])

  useEffect(() => {
    if (!!searchText && searchDone) {
      searchTemplates({
        variables: {
          term: searchText,
          filter: {
            type: selectedType,
          },
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTemplates, selectedType])

  useEffect(() => {
    if (otherSelected && !enterEmailModalVisible) {
      setSearchInputFocused(true)
    }
  }, [otherSelected, enterEmailModalVisible])

  useEffect(() => {
    if (templateIdOrSlug || viewType === FormBuilderViews.preview) {
      firstTimeTabChange.current = true
    } else {
      setFormData(undefined)
    }
  }, [templateIdOrSlug, viewType])

  const trackFormUpdated = debounce(() => {
    analytics.track(SegmentEvent.FORM_BUILDER_FORM_UPDATED, { formTitle: creator.JSON?.title })
  }, 500)

  useEffect(() => {
    creator.onUploadFile.add(async (_, options) => {
      options.callback('error', '')
      toast.show({
        autohide: false,
        state: 'error',
        message: `Sign-up to unlock this feature.`,
      })
    })

    creator.onActiveTabChanged.add((_sender, options) => {
      if (!firstTimeTabChange.current || options.tabName === 'designer') {
        analytics.track(SegmentEvent.FORM_BUILDER_FORM_TAB_CHANGED, {
          tab: options.tabName === 'test' ? 'preview' : options.tabName,
          formTitle: creator.JSON.title,
        })
      }
      firstTimeTabChange.current = false
    })

    creator.onStateChanged.add((_modal, _options) => {
      trackFormUpdated()
    })

    creator.onPropertyChanged.add((_modal, options) => {
      if (options?.newValue?.jsonObj) {
        setChatMessageCurrentContent(JSON.stringify(options.newValue.jsonObj))
      }
    })

    creator.onModified.add((modal) => {
      if (modal?.JSON) {
        setChatMessageCurrentContent(JSON.stringify(modal.JSON))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creator])

  useEffect(() => {
    if (formData?.content) {
      creator.text = formData.content
      creator.activeTab = 'test'
    }
  }, [creator, formData])

  const filteredData = useMemo(() => {
    if ((searchDone || selectedTopics.length) && searchTemplateData?.searchDefaultTemplates) {
      return searchTemplateData?.searchDefaultTemplates
    }
    return shuffledDefaultLibraryData
  }, [
    searchDone,
    selectedTopics,
    searchTemplateData?.searchDefaultTemplates,
    shuffledDefaultLibraryData,
  ])

  const handleScroll = useCallback(() => {
    if (window.scrollY > 800 && !showScrollToTopButton) {
      setShowScrollToTopButton(true)
    } else if (window.scrollY <= 800 && showScrollToTopButton) {
      setShowScrollToTopButton(false)
    }
  }, [showScrollToTopButton])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const canPreviewActivities = useMemo(() => {
    return !meData?.me?.organization || getActivityPreviewTimes() < 5 || isUmoreUser
  }, [meData?.me, isUmoreUser])

  const canFetchMore = useMemo(() => {
    return (
      defaultLibraryData?.templates?.total &&
      defaultLibraryData?.templates.data?.length &&
      defaultLibraryData?.templates.data?.length < defaultLibraryData?.templates?.total
    )
  }, [defaultLibraryData?.templates])

  const fetchMoreTemplates = useCallback(() => {
    if (canFetchMore) {
      skip.current += limit
      fetchMore({
        variables: {
          isDefault: true,
          filter: {
            type: selectedType,
            skip: skip.current,
            limit,
          },
        },
      })
    }
  }, [canFetchMore, fetchMore, selectedType])

  const handleOnShowSignUpModal = useCallback(
    (source: SignUpSource, form?: DefaultTemplatesQueryElement) => () => {
      const selectedFormData = formData || form
      // For free plan workflow we navigate to register directly
      // if (selectedFormData) {
      //   savedForm.setSaveForm({
      //     ...selectedFormData,
      //     content: creator.text || selectedFormData.content,
      //   })
      // }
      // navigate('/register')

      setSignUpModalVisible(true)

      let eventName: SegmentEvent | undefined = undefined
      if (source === 'Send To Client') {
        eventName = SegmentEvent.FORM_BUILDER_SEND_TO_CLIENT_BUTTON_CLICKED
      } else if (source === 'Add To Library') {
        eventName = SegmentEvent.FORM_BUILDER_ADD_TO_LIBRARY_BUTTON_CLICKED
      } else if (source === 'Download PDF') {
        eventName = SegmentEvent.FORM_BUILDER_DOWNLOAD_PDF_BUTTON_CLICKED
      }
      if (eventName) {
        analytics.track(eventName, {
          formTitle: selectedFormData?.title || creator?.JSON?.title,
          formType: selectedFormData?.type,
          isFormGenerated: selectedFormData?.id === 'custom',
        })
      }

      setSignUpSource(source)

      analytics.track(SegmentEvent.FORM_BUILDER_SIGN_UP_MODAL_OPENED, {
        formTitle: selectedFormData?.title || creator?.JSON?.title,
        formType: selectedFormData?.type,
        isFormGenerated: selectedFormData?.id === 'custom',
        source,
      })
    },
    [formData, setSignUpModalVisible, creator?.JSON?.title],
  )

  const handleOnClickPreview = useCallback(
    (data: DefaultTemplatesQueryElement) => () => {
      if (canPreviewActivities) {
        setFormData(data)
        if (data?.id === 'custom') {
          navigate(`${pathname}?view=${FormBuilderViews.preview}`)
        } else {
          if (data?.slug) {
            navigate(`${pathname}/${data?.slug}`)
          } else {
            navigate(`${pathname}/${data?.id}`)
          }
        }

        analytics.track(SegmentEvent.FORM_BUILDER_ACTIVITY_PREVIEWED, {
          formTitle: data?.title || JSON.parse(data?.content || '{}')?.title,
          formType: data?.type,
          isFormGenerated: data?.id === 'custom',
        })
        incrementActivityPreviewTimes()
        if (previewTimes.current === 1) {
          handleOnShowSignUpModal('Automatic')
        }
        previewTimes.current += 1
      } else {
        handleOnShowSignUpModal('Limit Reached', data)
      }
    },
    [canPreviewActivities, pathname, handleOnShowSignUpModal, navigate],
  )

  const handleClickAskUsForHelp = useCallback(
    () => window.open(`${hubSpotContactUsForm}`, '_blank'),
    [],
  )

  const handleGeneratePrompts = useCallback(async () => {
    const input = `${selectedPromptType ? `- Type: ${selectedPromptType}` : ''}
${selectedAudiences.length ? `- Audiences: ${selectedAudiences.join(', ')}` : ''}
${selectedPromptTopics.length ? `- Topics: ${selectedPromptTopics.join(', ')}` : ''}
${extraInfo ? `- Extra Information: ${extraInfo}` : ''}`

    generatePrompts({
      variables: { input },
      onError: (error) => {
        if (error.message.includes('Too many requests')) {
          toast.show({
            message: 'AI credit limit exceeded, please try again in an hour.',
            state: 'error',
          })
        } else {
          toast.show({
            message: 'An error happened while trying to generate Activity, please try again later',
            state: 'error',
          })
        }
      },
    })
  }, [
    generatePrompts,
    selectedPromptType,
    selectedAudiences,
    selectedPromptTopics,
    extraInfo,
    toast,
  ])

  const handleGenerateForm = useCallback(
    async (attachments?: string[]) => {
      if (canPreviewActivities) {
        const input = generatorText || searchText
        if (input || attachments) {
          generateFormData({
            variables: {
              input,
              attachments: attachments || undefined,
            },
            onCompleted: (data) => {
              let title = 'Generated Form: Untitled'

              if (isValidJSON(data?.generateFormData)) {
                title = `Generated Form: ${
                  JSON.parse(data?.generateFormData || '{}').title || 'Untitled'
                }`
              }
              handleOnClickPreview({
                id: 'custom',
                content: data.generateFormData,
                title,
                default: true,
                scorable: false,
                logo: 'https://umore-app-content.s3.eu-central-1.amazonaws.com/forms/custom-assignment-logo.svg',
              })()
            },
            onError: (error) => {
              if (error.message.includes('Too many requests')) {
                toast.show({
                  message: 'AI credit limit exceeded, please try again in an hour.',
                  state: 'error',
                })
              } else {
                toast.show({
                  message:
                    'An error happened while trying to generate Activity, please try again later',
                  state: 'error',
                })
              }
            },
          })
        }
      } else {
        handleOnShowSignUpModal('Limit Reached', {
          id: 'custom',
          title: '',
          default: true,
          scorable: false,
        })
      }
    },
    [
      canPreviewActivities,
      generateFormData,
      generatorText,
      handleOnClickPreview,
      handleOnShowSignUpModal,
      searchText,
      toast,
    ],
  )

  const handleOnCloseSignUpModal = useCallback(() => {
    setSignUpModalVisible(false)
  }, [])

  const handleOnClickSignUp = useCallback(
    ({ isYearly }: SubscriptionSwitchOnSubmit) => {
      dispatch({ type: 'set', isSubscriptionPeriodYearly: isYearly })
      if (formData) {
        savedForm.setSaveForm({
          ...formData,
          content: creator.text || formData.content,
        })
      }
      analytics.track(SegmentEvent.FORM_BUILDER_SIGN_UP_BUTTON_CLICKED, {
        source: signUpSource,
        isFormGenerated: formData?.id === 'custom',
        subscriptionPeriod: isYearly ? 'yearly' : 'monthly',
      })
      navigate('/register')
    },
    [creator.text, dispatch, formData, navigate, savedForm, signUpSource],
  )

  const onChangeSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setSearchDone(false)
    }
    setSearchText(e.target.value)
  }, [])

  const handleOnSearch = useCallback(() => {
    if (!searchLoading && !!searchText) {
      searchTemplates({
        variables: {
          term: searchText,
          filter: {
            type: selectedType,
          },
        },
        onCompleted: () => {
          setSearchDone(true)
          setSelectedTopics([])
        },
      })
    }
  }, [searchLoading, searchTemplates, searchText, selectedType])

  // const handleYoutubeStateChanged = useCallback(
  //   (event: YouTubeEvent<number>) => {
  //     if (event.data === -1) {
  //       analytics.track(SegmentEvent.FORM_BUILDER_VIDEO_STARTED, { path: pathname })
  //     } else if (event.data === 0) {
  //       analytics.track(SegmentEvent.FORM_BUILDER_VIDEO_ENDED, { path: pathname })
  //     }
  //   },
  //   [pathname],
  // )

  const handleChangeSelectedType = useCallback(
    (templateType: TemplateType) => () => {
      skip.current = 0
      setSelectedType(templateType)
    },
    [],
  )

  const _renderFormItem = useCallback(
    (item: DefaultTemplatesQueryElement) => {
      return (
        <CCol key={item?.id}>
          <CLink
            href={`${pathname}/${item?.slug ? item?.slug : item?.id}`}
            title={item?.title}
            onClick={(e) => {
              if (!isCrawler()) {
                e.preventDefault()
              }
              handleOnClickPreview(item)()
            }}
            style={{ color: 'unset', textDecoration: 'none' }}
          >
            <FormTemplateListItem
              hideButtons
              title={item?.title || 'Untitled'}
              description={item?.description}
              tags={item?.tags}
              image={
                item?.logo ||
                'https://umore-app-content.s3.eu-central-1.amazonaws.com/forms/custom-assignment-logo.svg'
              }
              type={item?.type}
              onClick={() => {}}
            />
          </CLink>
        </CCol>
      )
    },
    [handleOnClickPreview, pathname],
  )
  const _renderSignUpModalTitle = useMemo(() => {
    if (meData?.me?.organization && !isUmoreUser) {
      return 'Access from Your Account '
    }
    if (!canPreviewActivities) {
      return 'You’ve reached your preview limit'
    }
    return null
  }, [meData?.me, isUmoreUser, canPreviewActivities])

  const _renderSignUpModalContent = useMemo(() => {
    if (meData?.me && !isUmoreUser) {
      return (
        <>
          <div className="title-font text-center mb-4">
            You have access to this action from your own uMore Dashboard.
          </div>
          <WomanWorkingImage className="align-self-center mb-4" />
          <CLoadingButton
            className="w-100 mb-4"
            onClick={() => {
              navigate('/create', { replace: true })
            }}
          >
            Take Me to My Dashboard
          </CLoadingButton>
        </>
      )
    }
    return (
      <>
        <SubscriptionSwitch
          showDiscountCodeCheck={false}
          showFreePlan={false}
          className="mb-3 text-center"
          onSubmit={handleOnClickSignUp}
          buttonText="Sign Up Now"
        />
        <CButton
          variant="outline"
          className="w-100"
          onClick={() => {
            analytics.track(SegmentEvent.BOOK_A_DEMO_BUTTON_CLICKED, {
              source: 'Demo Page Sign-up Modal',
            })
            window.open(bookADemoLink, '_blank')
          }}
        >
          Book A Demo
        </CButton>
      </>
    )
  }, [meData?.me, isUmoreUser, navigate, handleOnClickSignUp])

  const _renderBackButton = useMemo(
    () => (
      <div
        className="d-flex align-self-start clickable align-items-center"
        onClick={() => {
          if (pathname.includes('/demo-generator')) {
            navigate(`/demo-generator`, { replace: true })
          } else {
            navigate(`/demo`, { replace: true })
          }
        }}
      >
        <Icon path={mdiArrowLeft} size={1} color={black} />
        <div className="button-font ms-3">Back to library</div>
      </div>
    ),
    [navigate, pathname],
  )

  const _renderStickyBanner = useMemo(() => {
    // if (isMobile) {
    //   if (desktopBannerVisible) {
    //     return (
    //       <div className="FBSignUpSticky">
    //         <div className="d-flex align-items-center justify-content-start w-100">
    //           <div>
    //             <DesktopExperienceSketch
    //               style={{ height: '50px', width: '50px' }}
    //               className="me-1"
    //             />
    //           </div>
    //           <div className="flex-1">
    //             <div className="overline-font">We’re better on desktop</div>
    //             <div className="body-large-font">www.umore.app</div>
    //           </div>
    //           {navigator.share ? (
    //             <div
    //               className="me-2"
    //               onClick={() => {
    //                 analytics.track(SegmentEvent.FORM_BUILDER_MOBILE_SHARE_BUTTON_CLICKED)
    //                 navigator.share({
    //                   text: 'uMore | Create and Send CBT Homework',
    //                   url: 'https://www.umore.app',
    //                 })
    //               }}
    //             >
    //               <Icon path={mdiExportVariant} size={1} color={battleshipGrey} />
    //             </div>
    //           ) : null}

    //           <div onClick={() => setDesktopBannerVisible(false)}>
    //             <Icon path={mdiClose} size={1} color={battleshipGrey} />
    //           </div>
    //         </div>
    //       </div>
    //     )
    //   }
    // }
    return null
  }, [])

  const _renderTopicButton = useCallback(
    ({
      topic,
      isSelected,
      onClick,
      showCloseButton,
      hideSelectedState,
    }: {
      topic: string
      isSelected?: boolean
      onClick?: () => void
      showCloseButton?: boolean
      hideSelectedState?: boolean
    }) => {
      const selected =
        isSelected ||
        ((selectedTopics.includes(topic) || (topic === OTHER && otherSelected)) &&
          !hideSelectedState)

      return (
        <CCol
          className="me-1"
          key={topic}
          xs="auto"
          onClick={() => {
            if (onClick) {
              onClick()
            } else if (topic === OTHER) {
              setOtherSelected((prev) => !prev)
            } else if (!selected) {
              setSelectedTopics((prev) => [...prev, topic])
            } else {
              setSelectedTopics((prev) => prev.filter((item) => item !== topic))
            }
          }}
        >
          <div
            className={`${
              selected ? 'FBTemplateTypeButtonSelected' : 'FBTemplateTypeButton'
            } d-flex`}
          >
            <div className="overline-font">{topic}</div>
            {selected && showCloseButton ? (
              <div className="ms-1 d-flex">
                <Icon size={0.6} path={mdiClose} />
              </div>
            ) : null}
          </div>
        </CCol>
      )
    },
    [otherSelected, selectedTopics],
  )

  const metaData = useMemo(() => {
    const customTitle = featureFlagPayload?.title
    const customDescription = featureFlagPayload?.description

    if (viewType === FormBuilderViews.generator) {
      return {
        header:
          customTitle || 'Easily Create Custom Therapy Worksheets and Assessments for Your Clients',
        description:
          customDescription ||
          'Create and Send assignments as interactive links to clients, and view their results - saving you time and effort!',
        inputHeader: 'Describe what you want to create',
        enterEmailModalTitle: 'Create and send effective therapy assignments in minutes 🌟',
        enterEmailModalButton: 'Get Access Now',
        youtubeVideoId: 'f6Pt3DqhDk8',
      }
    }
    return {
      header: customTitle || 'Send Clients Therapy Worksheets Effortlessly & Save Time on Admin',
      description:
        customDescription ||
        'Send assignments as interactive links to clients, and view their results - saving you time and effort!',
      inputHeader: 'Describe what you want to create',
      enterEmailModalTitle: 'Access to hundreds of interactive therapy resources 🌟',
      enterEmailModalButton: 'View Therapy Resources',
      youtubeVideoId: 'uqbezGTiNWU',
    }
  }, [viewType, featureFlagPayload])

  const _renderTopics = useMemo(() => {
    return (
      <div className="FBBFormItemsContainer mt-2 mb-2">
        <div className="d-flex mb-1">
          {allTopics.slice(0, 8).map((topic) =>
            _renderTopicButton({
              topic,
              showCloseButton: false,
              hideSelectedState: enterEmailModalVisible,
            }),
          )}
        </div>
        <div className="d-flex">
          {allTopics.slice(8).map((topic) =>
            _renderTopicButton({
              topic,
              showCloseButton: false,
              hideSelectedState: enterEmailModalVisible,
            }),
          )}
        </div>
      </div>
    )
  }, [_renderTopicButton, enterEmailModalVisible, allTopics])

  const _renderContent = useMemo(() => {
    if (templateIdOrSlug || viewType === FormBuilderViews.preview) {
      if (!formData) {
        return (
          <div className="FBBPreviewContainer">
            <div className="d-flex column w-100 align-items-center px-2 px-lg-4 mb-2 mt-3">
              {_renderBackButton}
            </div>
            <div className="d-flex flex-1 justify-content-center align-items-center">
              <CSpinner color="primary" variant="grow" />
            </div>
          </div>
        )
      }
      return (
        <div className="FBBPreviewContainer">
          {formData.id !== 'custom' ? (
            <Helmet prioritizeSeoTags>
              <title>{formData.title}</title>
              <meta property="og:title" content={formData.title} />
              <meta property="og:description" content={formData.description || ''} />
              <meta property="og:url" content={window.location.href} />
              <meta property="twitter:title" content={formData.title} />
              <meta property="twitter:description" content={formData.description || ''} />
              <meta property="twitter:image" content={formData.logo || ''} />
            </Helmet>
          ) : null}
          <div className="w-100 d-flex justify-content-between align-items-center px-2 px-lg-4 mb-2 mt-3">
            {_renderBackButton}
            <div className="d-flex">
              <CButton
                className="me-2 d-flex align-items-center"
                variant="outline"
                onClick={handleOnShowSignUpModal('Download PDF')}
              >
                <Icon path={mdiTrayArrowDown} size={0.8} />
                <div className="ms-2 d-none d-md-block">Download PDF</div>
              </CButton>
              <CButton
                className="d-flex align-items-center"
                onClick={handleOnShowSignUpModal('Send To Client')}
              >
                <Icon path={mdiSend} size={0.8} />
                <div className="ms-2 d-none d-md-block">Send to Client</div>
              </CButton>
            </div>
          </div>
          <SurveyCreatorComponent creator={creator} />
          <ChatMessageButton
            selectedTemplate={formData}
            currentContent={chatMessageCurrentContent || formData.content || undefined}
            onClickForm={(content) => {
              toast.show({ message: 'Assignment updated' })
              creator.text = content
            }}
          />
        </div>
      )
    }
    return (
      <div className="d-flex column">
        <Helmet prioritizeSeoTags>
          <title>uMore</title>
        </Helmet>
        {_renderStickyBanner}
        <div className="FBBGeneratorContainer">
          <CTooltip content="Contact us">
            <div className="FBBFabButton clickable" onClick={handleClickAskUsForHelp}>
              <Icon path={mdiEmail} size={1} color={white} />
            </div>
          </CTooltip>
          {showScrollToTopButton && (
            <CButton
              variant="outline"
              className="FBScrollToTopButton"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
            >
              <div className="d-flex align-content-center justify-content-center">
                <div>Back To Top</div>
                <Icon className="ms-2" path={mdiArrowUp} size={0.7} />
              </div>
            </CButton>
          )}
          <CCol lg={9} md={10} xs={12} className="d-flex column align-items-center">
            <div className="w-100 d-flex justify-content-between align-items-center mb-3">
              <div>
                <AppLogo size={20} className="column" textClassName="ms-0 body-small-bold-font" />
              </div>
              <div className="d-flex flex-wrap ms-2 justify-content-center">
                <CButton
                  className="me-2 mb-2"
                  variant="outline"
                  onClick={() => {
                    window.open(uMorePricingLink, '_blank')
                  }}
                >
                  Pricing
                </CButton>
                <CButton
                  className="me-2 mb-2 d-none d-sm-block"
                  color="primary"
                  variant="outline"
                  onClick={() => {
                    analytics.track(SegmentEvent.BOOK_A_DEMO_BUTTON_CLICKED, {
                      source: 'Demo Page Header Button',
                    })
                    window.open(bookADemoLink, '_blank')
                  }}
                >
                  Book a Demo
                </CButton>
                <CButton
                  className="me-2 mb-2"
                  color="primary"
                  variant="outline"
                  onClick={() => {
                    navigate('/login')
                  }}
                >
                  Login
                </CButton>
                <CButton
                  className="mb-2"
                  color="primary"
                  onClick={() => {
                    navigate('/register')
                  }}
                >
                  Get a FREE trial
                </CButton>
              </div>
            </div>
            <div className="FBLibraryContainer">
              {/* Mobile version */}
              <div className="d-lg-none d-md-block text-center">
                <div className="d-flex column align-items-center">
                  <div className="title-font mb-2 mt-5">{metaData.header}</div>
                  {/* <div className="body-large-font mb-4">{metaData.description}</div> */}
                  {isMobile && (
                    <img
                      src={require('@assets/images/umore-incentive.png')}
                      style={{ width: '60%', aspectRatio: 1.75 }}
                      alt="uMore Incentive"
                    />
                  )}
                  {/* <CButton
                    variant="outline"
                    onClick={() => {
                      window.open(`https://youtu.be/${metaData.youtubeVideoId}`, '_blank')
                    }}
                  >
                    Watch Demo Video
                  </CButton> */}
                  {/* <YouTube
                    className="mb-4 FBBYoutube"
                    opts={{
                      width: '100%',
                      height: '100%',
                      allowFullScreen: true,
                    }}
                    videoId={metaData.youtubeVideoId}
                    onStateChange={handleYoutubeStateChanged}
                  /> */}
                </div>
                {/* <div className="body-large-bold-font mt-5">{metaData.inputHeader}</div> */}
              </div>
              {/* Desktop version */}
              <div className="d-none d-lg-block">
                <CRow>
                  <CCol lg={5}>
                    <h3 style={{ color: darkGreen, width: '100%', lineHeight: 1.2 }}>
                      <ScaleText minFontSize={20} maxFontSize={37}>
                        <span>{metaData.header}</span>
                      </ScaleText>
                    </h3>
                    <div className="subtitle-font mb-4 white-space-pre-wrap">
                      {metaData.description}
                    </div>
                  </CCol>
                  <CCol className="d-flex justify-content-center align-items-center" lg={7}>
                    <img
                      src={require('@assets/images/umore-incentive.png')}
                      style={{ width: '80%', aspectRatio: 1.75 }}
                      alt="uMore Incentive"
                    />
                    {/* <YouTube
                      className="mb-4 FBBYoutube"
                      opts={{
                        width: '100%',
                        height: '100%',
                        allowFullScreen: true,
                      }}
                      videoId={metaData.youtubeVideoId}
                      onStateChange={handleYoutubeStateChanged}
                    /> */}
                  </CCol>
                </CRow>
              </div>
              {/* Start generator view */}
              {viewType === FormBuilderViews.generator && (
                <>
                  <div className="title-font text-center mt-5">{metaData.inputHeader}</div>
                  <CCol className="d-flex column align-items-center">
                    <CCol lg={10} xs={12} className="d-flex column">
                      <GeneratorInput
                        withAttachment
                        containerClassName="mt-2 mb-2"
                        autoFocus
                        sendButtonText={!isMobile ? 'Generate' : undefined}
                        disabled={!generatorText}
                        value={generatorText}
                        onChange={(e) => setGeneratorText(e.target.value)}
                        onSubmit={handleGenerateForm}
                        placeholder="Type here..."
                      />
                      <CButton
                        variant={promptHelperViewVisible ? 'outline' : undefined}
                        className="body-large-bold-font w-100 align-self-center m-md-2"
                        onClick={() => {
                          setPromptHelperViewVisible((prev) => !prev)
                          analytics.track(SegmentEvent.FORM_BUILDER_SELECT_PROMPT_VIEW_TOGGLED, {
                            shown: !promptHelperViewVisible,
                          })
                        }}
                        style={
                          promptHelperViewVisible
                            ? { borderWidth: 0, backgroundColor: 'transparent' }
                            : undefined
                        }
                      >
                        Use our description guide
                        <Icon
                          className="ms-3"
                          path={promptHelperViewVisible ? mdiUnfoldLessHorizontal : mdiChevronDown}
                          size={0.8}
                        />
                      </CButton>
                    </CCol>
                    {promptHelperViewVisible && (
                      <div
                        className="w-100"
                        style={{
                          border: `solid 1px ${middleBlue}`,
                          borderRadius: 5,
                          padding: 20,
                          overflowY: 'auto',
                        }}
                      >
                        <div className="body-large-font mb-4">
                          Select categories for personalized examples
                        </div>
                        <CRow xs={{ gutterY: 3 }}>
                          <CCol md={5} sm={12}>
                            <div className="body-small-bold-font  mb-1">Assignment type</div>
                            <CRow className="mb-1" xs={{ gutter: 1 }}>
                              {promptTypes.map((item) =>
                                _renderTopicButton({
                                  showCloseButton: true,
                                  topic: item,
                                  isSelected: selectedPromptType === item,
                                  onClick: () => {
                                    if (selectedPromptType !== item) {
                                      setSelectedPromptType(item)
                                    } else {
                                      setSelectedPromptType('')
                                    }
                                  },
                                }),
                              )}
                            </CRow>
                            <div className="body-small-bold-font mt-3  mb-1">Audience</div>
                            <CRow className="mb-1" xs={{ gutter: 1 }}>
                              {promptAudiences.map((topic) =>
                                _renderTopicButton({
                                  showCloseButton: true,
                                  topic: topic,
                                  isSelected: selectedAudiences.includes(topic),
                                  onClick: () => {
                                    if (!selectedAudiences.includes(topic)) {
                                      setSelectedAudiences((prev) => [...prev, topic])
                                    } else {
                                      setSelectedAudiences((prev) =>
                                        prev.filter((item) => item !== topic),
                                      )
                                    }
                                  },
                                }),
                              )}
                            </CRow>
                            <div className="body-small-bold-font mt-3  mb-1">Topic</div>
                            <CRow className="mb-1" xs={{ gutter: 1 }}>
                              {promptTopics.map((topic) =>
                                _renderTopicButton({
                                  showCloseButton: true,
                                  topic: topic,
                                  isSelected: selectedPromptTopics.includes(topic),
                                  onClick: () => {
                                    if (!selectedPromptTopics.includes(topic)) {
                                      setSelectedPromptTopics((prev) => [...prev, topic])
                                    } else {
                                      setSelectedPromptTopics((prev) =>
                                        prev.filter((item) => item !== topic),
                                      )
                                    }
                                  },
                                }),
                              )}
                            </CRow>
                            <div className="body-small-bold-font mt-3 mb-1">Add more details</div>
                            <CFormInput
                              size="sm"
                              onChange={(e) => {
                                setExtraInfo(e.target.value)
                              }}
                              value={extraInfo}
                              placeholder="Add specific asks or topics"
                            />
                            <CLoadingButton
                              className="w-100 mt-3"
                              loading={promptsLoading}
                              onClick={handleGeneratePrompts}
                              variant={
                                (selectedPromptType ||
                                  selectedAudiences.length ||
                                  selectedPromptTopics.length ||
                                  extraInfo) &&
                                !generatorText
                                  ? undefined
                                  : 'outline'
                              }
                            >
                              See Examples
                            </CLoadingButton>
                          </CCol>
                          <CCol md={7} sm={12}>
                            <div className="body-small-bold-font mb-1">Prompt Examples</div>
                            {promptsLoading && !promptsData?.generatePrompts ? (
                              <>
                                <SkeletonLoader type={LoaderType.Prompt} />
                                <SkeletonLoader className="mt-3" type={LoaderType.Prompt} />
                                <SkeletonLoader className="mt-3" type={LoaderType.Prompt} />
                              </>
                            ) : (
                              (promptsData?.generatePrompts || promptExamples).map((prompt) => (
                                <div
                                  className="FBTemplateTypeButton p-2 w-100 mb-3"
                                  key={prompt}
                                  onClick={() => {
                                    setGeneratorText(prompt || '')
                                    setTimeout(
                                      () => window.scrollTo({ top: 200, behavior: 'smooth' }),
                                      100,
                                    )

                                    analytics.track(
                                      SegmentEvent.FORM_BUILDER_PROMPT_EXAMPLE_SELECTED,
                                      {
                                        prompt,
                                      },
                                    )
                                  }}
                                >
                                  <div>{prompt}</div>
                                  <Icon
                                    className="align-self-end"
                                    path={mdiArrowTopLeft}
                                    size={0.8}
                                    color={pictonBlue}
                                  />
                                </div>
                              ))
                            )}
                          </CCol>
                        </CRow>
                      </div>
                    )}
                  </CCol>
                  <div
                    className="title-font text-center align-self-center"
                    style={{
                      width: '90%',
                      height: 1,
                      backgroundColor: middleBlue,
                      marginTop: '5rem',
                    }}
                  />
                </>
              )}
              {/* End generator view */}
              {/* Start library view */}
              <>
                <div className="w-100">{_renderTopics}</div>
                {!searchInputFocused ? (
                  <div
                    className="FBFakeSearchInput body-small-font"
                    onClick={() => {
                      setSearchInputFocused(true)
                    }}
                  >
                    Search Topic
                    <Icon path={mdiMagnify} size={0.8} className="ms-3" color={middleBlue} />
                  </div>
                ) : (
                  <SearchInput
                    autoFocus
                    containerClassName="body-small-font mb-3"
                    onBlur={() => {
                      if (!searchText) {
                        setSearchInputFocused(false)
                      }
                    }}
                    value={searchText}
                    onChange={onChangeSearch}
                    placeholder="Search Topic"
                    onSubmit={handleOnSearch}
                  />
                )}
                <div className="d-flex mt-4 mb-4" style={{ overflowX: 'auto', maxWidth: '85vw' }}>
                  {[
                    {
                      title: 'Worksheets',
                      active: selectedType === TemplateType.Worksheet,
                      onClick: handleChangeSelectedType(TemplateType.Worksheet),
                    },
                    {
                      title: 'Techniques',
                      active: selectedType === TemplateType.Technique,
                      onClick: handleChangeSelectedType(TemplateType.Technique),
                    },
                    {
                      title: 'Assessments',
                      active: selectedType === TemplateType.Assessment,
                      onClick: handleChangeSelectedType(TemplateType.Assessment),
                    },
                    {
                      title: 'Intake forms',
                      active: selectedType === TemplateType.Intake,
                      onClick: handleChangeSelectedType(TemplateType.Intake),
                    },
                  ].map((tab) => (
                    <div key={tab.title} className="clickable" onClick={tab.onClick}>
                      <div
                        className="body-small-bold-font mx-3"
                        style={{
                          color: !tab.active ? battleshipGrey : undefined,
                        }}
                      >
                        {tab.title}
                      </div>
                      <div
                        style={{
                          marginTop: 10,
                          width: '100%',
                          height: 3,
                          backgroundColor: tab.active ? battleshipGrey : culturedGrey,
                        }}
                      />
                    </div>
                  ))}
                </div>
                <CRow className="mb-3" xs={{ gutter: 1 }}>
                  {!enterEmailModalVisible
                    ? selectedTopics.map((topic) =>
                        _renderTopicButton({ topic, showCloseButton: true }),
                      )
                    : null}
                </CRow>
                {searchLoading || libraryLoading ? (
                  <CRow
                    className="px-lg-2"
                    xl={{ cols: 4, gutter: 3 }}
                    lg={{ cols: 2, gutter: 3 }}
                    sm={{ cols: 2, gutter: 3 }}
                    xs={{ cols: 1, gutter: 3 }}
                  >
                    <SkeletonLoader />
                    <SkeletonLoader />
                    <SkeletonLoader />
                    <SkeletonLoader />
                  </CRow>
                ) : (
                  <>
                    <CRow
                      className="px-lg-2"
                      xl={{ cols: 4, gutter: 3 }}
                      lg={{ cols: 2, gutter: 3 }}
                      sm={{ cols: 2, gutter: 3 }}
                      xs={{ cols: 1, gutter: 3 }}
                    >
                      {viewType !== FormBuilderViews.generator && (
                        <CCol>
                          <div
                            className="FBCreateNewFormItemContainer clickable"
                            onClick={() => {
                              analytics.track(SegmentEvent.FORM_BUILDER_CREATE_BUTTON_CLICKED)
                              navigate('/demo-generator')
                            }}
                          >
                            <div className="text-button flex-1 d-flex column justify-content-center align-items-center py-5">
                              <Icon
                                className="mb-2"
                                path={mdiPlusCircleOutline}
                                size={1.5}
                                color={carolinaBlue}
                              />
                              <span>Create new assignment</span>
                            </div>
                            <div
                              style={{
                                width: '100%',
                                height: 1,
                                backgroundColor: lightGrey,
                              }}
                            />
                            <div className="px-2 py-3 body-small-bold-font">
                              Create a custom assignment
                            </div>
                          </div>
                        </CCol>
                      )}
                      {filteredData?.map(_renderFormItem)}
                      <Waypoint onEnter={fetchMoreTemplates} />
                    </CRow>
                  </>
                )}
              </>
              {/* End library view */}
            </div>
          </CCol>
        </div>
        <AppFooter />
      </div>
    )
  }, [
    templateIdOrSlug,
    viewType,
    _renderStickyBanner,
    handleClickAskUsForHelp,
    showScrollToTopButton,
    metaData.header,
    metaData.description,
    metaData.inputHeader,
    generatorText,
    handleGenerateForm,
    promptHelperViewVisible,
    extraInfo,
    promptsLoading,
    handleGeneratePrompts,
    selectedPromptType,
    selectedAudiences,
    selectedPromptTopics,
    promptsData?.generatePrompts,
    _renderTopics,
    searchInputFocused,
    searchText,
    onChangeSearch,
    handleOnSearch,
    selectedType,
    handleChangeSelectedType,
    enterEmailModalVisible,
    selectedTopics,
    searchLoading,
    libraryLoading,
    filteredData,
    _renderFormItem,
    fetchMoreTemplates,
    formData,
    _renderBackButton,
    handleOnShowSignUpModal,
    creator,
    chatMessageCurrentContent,
    toast,
    navigate,
    _renderTopicButton,
  ])

  return (
    <>
      {_renderContent}
      <CModal visible={loading} size="lg" alignment="center" backdrop="static">
        <CModalBody className="FBBLoadingModalContainer">
          <h5>Creating your Activity...</h5>
          <div className="body-large-font">Your Activity will be ready in a few seconds. 😊</div>
          <CSpinner variant="grow" className="mt-5" color="primary" />
          <div className="FBBSeparator w-50 mt-5" />
          <div className="body-large-font">
            Please note: This AI technology is in its early stages and may produce errors. It is
            your responsibility to review and verify all generated content before use.
          </div>
        </CModalBody>
      </CModal>

      <CModal
        onClose={handleOnCloseSignUpModal}
        visible={signUpModalVisible}
        alignment="center"
        style={{ backgroundColor: azureX }}
      >
        <CModalHeader className="p-4 pb-0 border-0">
          <CModalTitle>{_renderSignUpModalTitle}</CModalTitle>
        </CModalHeader>
        <CModalBody className="d-flex column px-3 p-md-4">{_renderSignUpModalContent}</CModalBody>
      </CModal>

      <CModal
        onClose={() => {
          setEnterEmailModalVisible(false)
        }}
        visible={enterEmailModalVisible}
        alignment="center"
        backdrop="static"
      >
        <CModalBody className="d-flex column align-items-center text-center">
          {/* <CCloseButton
            className="align-self-end"
            onClick={() => {
              setEnterEmailModalVisible(false)
              analytics.track(SegmentEvent.FORM_BUILDER_EMAIL_MODAL_CLOSED)
            }}
          /> */}
          <DesktopExperienceSketch className="mb-3" />
          <h5 className="mb-3">{metaData.enterEmailModalTitle}</h5>
          {viewType === FormBuilderViews.library && (
            <>
              <div className="body-large-font mb-2">Select your topic:</div>
              <CRow className="mb-4 justify-content-center" xs={{ gutter: 1 }}>
                {[...allTopics, OTHER].map((topic) =>
                  _renderTopicButton({
                    topic,
                    showCloseButton: false,
                  }),
                )}
              </CRow>
            </>
          )}
          <Controller
            name="email"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <CFormInput
                  className="body-small-font w-100"
                  placeholder="Enter your email"
                  value={field.value}
                  onChange={field.onChange}
                  invalid={!!errors.email}
                />
                {errors.email && (
                  <div className="caption-font generalTextError align-self-start ms-2">
                    {errors.email?.message}
                  </div>
                )}
              </>
            )}
          />
          <CButton
            className="w-100 mt-3"
            onClick={handleSubmit(() => {
              const { email } = getValues()
              const topics = [...selectedTopics]
              if (otherSelected) {
                topics.push(OTHER)
              }
              setFormBuilderEmail(email)
              setEnterEmailModalVisible(false)
              sendSegmentEvent({
                variables: {
                  eventName: SegmentEvent.FORM_BUILDER_EMAIL_PROVIDED,
                  properties: {
                    email,
                    topics,
                  },
                },
              })
            })}
          >
            {metaData.enterEmailModalButton}
          </CButton>
          <div className="button-font mt-3 w-100 text-center">
            Have an account?{' '}
            <CLink
              onClick={() => {
                navigate('/login')
              }}
              className="text-button"
              style={{ textDecoration: 'none' }}
            >
              Log in
            </CLink>
          </div>
        </CModalBody>
      </CModal>
    </>
  )
}

export default FormBuilder
